/**
 * Cookies Policy Page
 * World-class Cookies Policy with GDPR compliance
 * Public legal page accessible without authentication
 */

import LegalPageLayout from '@/components/legal/LegalPageLayout';
import { LEGAL_LAST_UPDATED } from '@/constants/legalVersions';

export default function CookiesPage() {
  return (
    <LegalPageLayout 
      title="Cookies Policy" 
      lastUpdated={LEGAL_LAST_UPDATED.cookies}
      metaDescription="Cookies Policy for FinList - Learn about the cookies and similar technologies we use, how they work, and how to manage your preferences."
    >
      {/* Section 1: What Are Cookies? */}
      <section>
        <h2>1. What Are Cookies?</h2>
        <p>
          Cookies are small text files that are placed on your device (computer, smartphone, or tablet) 
          when you visit a website or use an application. They are widely used to make websites and 
          applications work more efficiently and to provide information to site owners.
        </p>
        <p>
          Similar technologies include local storage, session storage, and pixels, which serve 
          comparable purposes.
        </p>
      </section>

      {/* Section 2: How We Use Cookies */}
      <section>
        <h2>2. How We Use Cookies</h2>
        <p>
          FinList uses cookies and similar technologies to provide, protect, and improve the Service. 
          We use them to:
        </p>
        <ul>
          <li>Keep you signed in and maintain your session</li>
          <li>Remember your preferences and settings</li>
          <li>Understand how you use FinList and identify areas for improvement</li>
          <li>Ensure the security and integrity of the Service</li>
          <li>Deliver a faster and more reliable experience</li>
        </ul>
      </section>

      {/* Section 3: Types of Cookies We Use */}
      <section>
        <h2>3. Types of Cookies We Use</h2>

        <h3>3.1 Strictly Necessary Cookies</h3>
        <p>
          These cookies are essential for the operation of FinList. Without them, the Service 
          cannot function properly.
        </p>
        <ul>
          <li><strong>Authentication cookies</strong> – keep you signed in securely</li>
          <li><strong>Session cookies</strong> – maintain your session state</li>
          <li><strong>Security cookies</strong> – detect and prevent fraud or malicious activity</li>
          <li><strong>Load balancing cookies</strong> – ensure optimal server performance</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <em>These cookies cannot be disabled as they are required for core functionality.</em>
        </p>

        <h3>3.2 Performance &amp; Analytics Cookies</h3>
        <p>
          These cookies help us understand how users interact with FinList, allowing us to 
          improve features and performance.
        </p>
        <ul>
          <li>Page views and navigation patterns</li>
          <li>Feature usage statistics</li>
          <li>Error tracking and diagnostics</li>
          <li>Load times and performance metrics</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <em>Data collected is aggregated and anonymized where possible.</em>
        </p>

        <h3>3.3 Preference Cookies</h3>
        <p>
          These cookies remember your settings and preferences to provide a personalized experience.
        </p>
        <ul>
          <li>Theme preference (light/dark mode)</li>
          <li>Currency and locale settings</li>
          <li>Dashboard layout preferences</li>
          <li>Notification preferences</li>
        </ul>
      </section>

      {/* Section 4: What We Do NOT Use */}
      <section>
        <h2>4. What We Do NOT Use</h2>
        <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4 my-4">
          <p className="font-semibold text-green-800 dark:text-green-200">✓ Your Privacy Matters</p>
          <p className="text-green-700 dark:text-green-300">
            FinList does <strong>NOT</strong> use cookies for:
          </p>
        </div>
        <ul>
          <li>Third-party advertising or ad tracking</li>
          <li>Cross-site tracking</li>
          <li>Selling data to advertisers or data brokers</li>
          <li>Building advertising profiles</li>
        </ul>
      </section>

      {/* Section 5: Third-Party Cookies */}
      <section>
        <h2>5. Third-Party Cookies</h2>
        <p>
          Some trusted third-party services integrated with FinList may set their own cookies. 
          These include:
        </p>
        <ul>
          <li><strong>Firebase / Google Cloud</strong> – authentication and infrastructure</li>
          <li><strong>Stripe</strong> – payment processing (only when making payments)</li>
          <li><strong>Analytics providers</strong> – aggregated usage statistics</li>
        </ul>
        <p>
          All third-party providers are contractually bound to protect your data and use cookies 
          only for their stated purposes. We carefully vet our partners to ensure they meet our 
          privacy standards.
        </p>
      </section>

      {/* Section 6: Cookie Duration */}
      <section>
        <h2>6. Cookie Duration</h2>
        <p>Cookies have different lifespans depending on their purpose:</p>
        <ul>
          <li>
            <strong>Session cookies</strong> – deleted when you close your browser
          </li>
          <li>
            <strong>Persistent cookies</strong> – remain on your device for a set period 
            (typically 30 days to 1 year) or until you delete them
          </li>
        </ul>
        <p>
          We only retain cookies for as long as necessary to fulfill their purpose.
        </p>
      </section>

      {/* Section 7: Your Cookie Choices */}
      <section>
        <h2>7. Your Cookie Choices</h2>
        <p>
          When you first visit FinList, you will see a cookie consent banner with the following options:
        </p>
        <ul>
          <li>
            <strong>Accept Essential Only</strong> – only cookies strictly necessary for the app 
            to function will be used
          </li>
          <li>
            <strong>Accept All Cookies</strong> – essential cookies plus analytics and preference 
            cookies will be enabled
          </li>
        </ul>
        <p>
          You can change your cookie preferences at any time through the <strong>Settings</strong> page 
          in your FinList account.
        </p>
      </section>

      {/* Section 8: Managing Cookies in Your Browser */}
      <section>
        <h2>8. Managing Cookies in Your Browser</h2>
        <p>
          Most web browsers allow you to control cookies through their settings. You can:
        </p>
        <ul>
          <li>View which cookies are stored on your device</li>
          <li>Delete some or all cookies</li>
          <li>Block cookies from specific sites</li>
          <li>Block all third-party cookies</li>
          <li>Clear cookies when you close your browser</li>
        </ul>
        <p>
          <strong>Note:</strong> Disabling essential cookies may prevent some features of FinList 
          from working correctly.
        </p>
        <p>
          For instructions on managing cookies in your browser, visit your browser's help documentation:
        </p>
        <ul>
          <li>
            <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="font-medium">
              Google Chrome
            </a>
          </li>
          <li>
            <a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="font-medium">
              Mozilla Firefox
            </a>
          </li>
          <li>
            <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="font-medium">
              Safari
            </a>
          </li>
          <li>
            <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="font-medium">
              Microsoft Edge
            </a>
          </li>
        </ul>
      </section>

      {/* Section 9: Updates to This Policy */}
      <section>
        <h2>9. Updates to This Policy</h2>
        <p>
          We may update this Cookies Policy from time to time to reflect changes in technology, 
          legislation, or our practices.
        </p>
        <ul>
          <li>The "Last Updated" date at the top of this page will be revised</li>
          <li>Material changes will be communicated through the Service</li>
          <li>We encourage you to review this policy periodically</li>
        </ul>
      </section>

      {/* Section 10: Contact Us */}
      <section>
        <h2>10. Contact Us</h2>
        <p>
          If you have any questions about our use of cookies or this Cookies Policy, please contact us:
        </p>
        <p className="text-lg">
          <a href="mailto:privacy@getfinlist.com" className="font-medium">
            📧 privacy@getfinlist.com
          </a>
        </p>
      </section>
    </LegalPageLayout>
  );
}
